import { SystemStyleObject } from '@chakra-ui/react'

import type { ColorScheme } from '../../types'

// Internal panel styling per color scheme.
// dropdownBg is hex because it goes into inline style={{}}.
// In light mode it is omitted so TypeaheadSearch falls back to its
// light semantic tokens (white bg, #f8f7f9 section-header bg).
export const panelTokens: Record<
  ColorScheme,
  {
    containerBg: string
    containerBorder: string
    dropdownBg: string | undefined
    boxShadow: string
    border: string | undefined
    height: string
  }
> = {
  dark: {
    containerBg: 'bg.input', // duskBlue.800
    containerBorder: 'border.search', // duskBlue.600
    dropdownBg: '#092738', // must match containerBg hex
    boxShadow: 'panel-dark',
    border: '1px solid',
    height: '266px',
  },
  light: {
    containerBg: 'light.bg.card', // white
    containerBorder: 'light.border.search',
    dropdownBg: undefined,
    boxShadow: 'panel-light',
    border: undefined,
    height: '252px',
  },
}

export const intentHeaderStyles: SystemStyleObject = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  px: '12px',
  py: '8px',
  bg: 'bg.action-bar-active',
  borderBottom: '1px solid',
  borderColor: 'border.action-bar',
}

export const backButtonStyles: SystemStyleObject = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  color: 'text.action-bar-label',
  p: '2px',
  flexShrink: 0,
  _hover: { color: 'white' },
  transition: 'color 0.15s ease',
}

export const intentPillStyles = (
  intent: 'add' | 'remove'
): SystemStyleObject => ({
  fontFamily: "'Soehne Mono', monospace",
  fontSize: '11px',
  fontWeight: '700',
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: intent === 'add' ? 'status.success-icon' : 'text.alert-danger-bold',
  userSelect: 'none',
})

export const cancelButtonStyles: SystemStyleObject = {
  fontFamily: "'Soehne', 'Söhne', sans-serif",
  fontSize: '13px',
  px: '12px',
  py: '5px',
  bg: 'transparent',
  border: '1px solid',
  borderColor: 'border.action-bar',
  borderRadius: '4px',
  cursor: 'pointer',
  color: 'text.action-bar-label',
  _hover: { borderColor: 'white', color: 'white' },
  transition: 'all 0.15s ease',
}

export const footerStyles = (colorScheme: ColorScheme): SystemStyleObject => ({
  borderTop: '1px solid',
  borderColor:
    colorScheme === 'light' ? 'light.border.subtle' : 'border.action-bar',
  px: '12px',
  py: '8px',
  display: 'flex',
  justifyContent: 'flex-end',
})

export const applyButtonStyles = (
  isDisabled: boolean,
  colorScheme: ColorScheme = 'dark'
): SystemStyleObject => ({
  fontFamily: "'Soehne', 'Söhne', sans-serif",
  fontSize: '13px',
  px: '12px',
  py: '5px',
  bg: isDisabled
    ? 'bg.action-bar-hover'
    : colorScheme === 'light'
    ? 'light.brand.primary'
    : 'interactive.primary',
  border: 'none',
  borderRadius: '4px',
  cursor: isDisabled ? 'not-allowed' : 'pointer',
  color: isDisabled ? 'text.search-placeholder' : 'white',
  opacity: isDisabled ? 0.6 : 1,
  _hover: isDisabled ? {} : { filter: 'brightness(1.1)' },
  transition: 'all 0.15s ease',
})
